/**
 * Multilingual version display component with i18n support
 */
import React, { useEffect, useState } from 'react';
import { Tag, Tooltip, Spin } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { getSystemVersion, SystemVersion } from '../../services/systemService';
import { localeService } from '../../services/localeService';

interface MultilingualVersionDisplayProps {
  showDetails?: boolean;
  style?: React.CSSProperties;
}

const MultilingualVersionDisplay: React.FC<MultilingualVersionDisplayProps> = ({ 
  showDetails = false,
  style 
}) => {
  const { t } = useTranslation();
  const [version, setVersion] = useState<SystemVersion | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const data = await getSystemVersion();
        setVersion(data);
      } catch (error) {
        console.error('Failed to fetch version:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVersion();
  }, []);

  if (loading) {
    return <Spin size="small" />;
  }

  if (!version) {
    return null;
  }

  const tooltipContent = (
    <div>
      <div>{t('system.databaseVersion', '数据库版本')}: {version.database_version}</div>
      {version.latest_migration && (
        <>
          <div style={{ marginTop: 8, borderTop: '1px solid rgba(255,255,255,0.2)', paddingTop: 8 }}>
            <div>{t('system.latestMigration', '最新迁移')}: {version.latest_migration.revision}</div>
            <div>{t('system.appliedAt', '应用时间')}: {localeService.formatDate(version.latest_migration.applied_at)}</div>
            {version.latest_migration.description && (
              <div>{t('system.description', '说明')}: {version.latest_migration.description}</div>
            )}
          </div>
        </>
      )}
    </div>
  );

  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, ...style }}>
      <Tag color="blue">
        {version.database_version}
      </Tag>
      {showDetails && (
        <Tooltip title={tooltipContent}>
          <InfoCircleOutlined style={{ cursor: 'pointer', color: '#1890ff' }} />
        </Tooltip>
      )}
    </div>
  );
};

// 默认导出
export default MultilingualVersionDisplay;

// 命名导出
export { MultilingualVersionDisplay };

// Footer 版本显示（多语言）
export const FooterMultilingualVersionDisplay: React.FC = () => (
  <MultilingualVersionDisplay showDetails={true} style={{ fontSize: '12px' }} />
);

// 版本名称显示组件
export const VersionNameDisplay: React.FC<{ style?: React.CSSProperties }> = ({ style }) => {
  const [version, setVersion] = useState<SystemVersion | null>(null);

  useEffect(() => {
    const fetchVersion = async () => {
      try {
        const data = await getSystemVersion();
        setVersion(data);
      } catch (error) {
        console.error('Failed to fetch version:', error);
      }
    };

    fetchVersion();
  }, []);

  if (!version) {
    return null;
  }

  return (
    <span style={style}>
      {version.database_version}
    </span>
  );
};
